// Refunds and discounts on rentals and sponsorships, approved in Hub and
// carried out by Booking (rentals and sponsorships plan, Phase 5; ADR 0045;
// decisions 6 and 13). Hub keeps its own request and its approval (ADR
// 0004); Booking keeps the money. Hub never shows a refund as made until
// Booking says so.

import { z } from 'zod'
import { REFUND_METHODS, type BookingRefundMethod } from './source-booking-commands'

export const BOOKING_MONEY_KINDS = ['refund', 'discount'] as const
export type BookingMoneyKind = (typeof BOOKING_MONEY_KINDS)[number]
export const BOOKING_MONEY_STATUSES = ['draft', 'submitted', 'approved', 'rejected', 'withdrawn'] as const
export type BookingMoneyStatus = (typeof BOOKING_MONEY_STATUSES)[number]
// Where carrying out an approved request stands: never sent; sent with no
// answer yet; Booking applied it; Booking accepted it and Stripe reports it
// pending; Booking refused it; unknown (Booking didn't answer, rule 12).
export const BOOKING_MONEY_EXECUTIONS = ['not_sent', 'sending', 'applied', 'pending_provider', 'refused', 'unknown'] as const
export type BookingMoneyExecution = (typeof BOOKING_MONEY_EXECUTIONS)[number]

// An attempt still open after this long lost its answer: it is shown as
// outcome unknown, and the only way on is to ask Booking what it holds.
export const MONEY_ATTEMPT_STALE_SECONDS = 120

const Id = z.string().min(1).max(64)
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const Cents = z.number().int().min(0).max(100_000_000)

// What the browser sends to ask for a refund. Hub reads Booking live, checks
// the payment and what is left to refund on it (less Hub's own requests
// still open on that payment), freezes what Booking said and asks the
// approvers.
export const CreateRefundRequest = z.object({
  kind: z.literal('refund'),
  recordKind: z.enum(['rental', 'sponsorship']),
  recordId: Id,
  invoiceId: Id,
  paymentId: Id,
  amountCents: z.number().int().min(1).max(100_000_000),
  method: z.enum(REFUND_METHODS),
  reason: z.string().trim().min(1).max(1000),
  approverPersonIds: z.array(z.string().min(1)).min(1).max(5),
  note: z.string().trim().max(2000).optional(),
})
export type CreateRefundRequest = z.infer<typeof CreateRefundRequest>

// What the browser sends to ask for a discount over the threshold (the
// quote's other pricing fields as they would be set with it).
export const CreateDiscountRequest = z.object({
  kind: z.literal('discount'),
  recordKind: z.literal('rental'),
  recordId: Id,
  discountCents: z.number().int().min(1).max(100_000_000),
  discountNote: z.string().trim().max(500).nullable().optional(),
  depositCents: Cents.nullable(),
  depositDueDate: Day.nullable(),
  reason: z.string().trim().min(1).max(1000),
  approverPersonIds: z.array(z.string().min(1)).min(1).max(5),
  note: z.string().trim().max(2000).optional(),
})
export type CreateDiscountRequest = z.infer<typeof CreateDiscountRequest>

export const CreateBookingMoneyRequest = z.discriminatedUnion('kind', [CreateRefundRequest, CreateDiscountRequest])
export type CreateBookingMoneyRequest = z.infer<typeof CreateBookingMoneyRequest>

// What Booking said when the request was made, frozen with it.
export type BookingMoneySnapshot = {
  asOf: string
  recordVersion: string
  recordStatus: string
  // A refund:
  invoice?: { id: string; number: string | null; status: string; totalCents: number; paidCents: number }
  payment?: { id: string; amountCents: number; method: string; status: string; refundedCents: number; committedCents: number; refundableCents: number; paidAt: string | null; viaStripe: boolean }
  // A discount:
  pricing?: { subtotalCents: number; discountCents: number; discountNote: string | null; totalCents: number; depositCents: number | null; depositDueDate: string | null }
}

export type BookingMoneyAttemptView = {
  id: string
  commandId: string
  commandType: 'booking.refund.execute' | 'booking.rental.pricing.set'
  actor: { personId: string; name: string | null }
  startedAt: string
  outcome: 'applied' | 'duplicate' | 'refused' | 'unknown' | 'not_configured' | 'observed' | null
  // An open attempt older than MONEY_ATTEMPT_STALE_SECONDS.
  stale: boolean
  code: string | null
  message: string | null
  finishedAt: string | null
}

export type BookingMoneyRequestView = {
  id: string
  kind: BookingMoneyKind
  record: { kind: 'rental' | 'sponsorship'; id: string; title: string | null }
  invoice: { id: string; number: string | null } | null
  paymentId: string | null
  method: BookingRefundMethod | null
  amountCents: number
  currency: 'USD'
  reason: string
  discount: { note: string | null; depositCents: number | null; depositDueDate: string | null; subtotalCents: number; percent: number } | null
  booking: BookingMoneySnapshot
  requestKey: string
  status: BookingMoneyStatus
  approval: { id: string; status: string; approvers: { personId: string; name: string | null; decision: 'approve' | 'reject' | null }[] } | null
  approvedAt: string | null
  approvedBy: { personId: string; name: string | null } | null
  execution: BookingMoneyExecution
  executionMessage: string | null
  bookingRefundId: string | null
  executedAt: string | null
  requestedBy: { personId: string; name: string | null }
  createdAt: string
  updatedAt: string
  version: number
  attempts: BookingMoneyAttemptView[]
  // What this person may do now.
  can: { send: boolean; check: boolean; withdraw: boolean; decide: boolean }
}

export type BookingMoneyApprover = { personId: string; name: string }

// A discount as a percent of the subtotal, to one decimal (for words only).
export const discountPercent = (discountCents: number, subtotalCents: number) => (subtotalCents > 0 ? Math.round((discountCents / subtotalCents) * 1000) / 10 : 100)

// Who may see money requests and their amounts.
export const canSeeBookingMoney = (has: (cap: 'rentals.money' | 'refunds.approve' | 'finance.read') => boolean) => has('rentals.money') || has('refunds.approve') || has('finance.read')
